/**
 * StayChangeConfirmDialog — la confirmació **única i obligatòria** dels gestos de la graella
 * (runbook 7, F3, decisió 5): moure una reserva de fila o allargar/escurçar-la estirant la vora.
 *
 * Res s'escriu en deixar anar. Aquí es diu amb text de debò què canviarà («Moure 99000001 de H301 a
 * H302 · 13→15 GEN (2 nits)», «Allargar 99000001 fins al 16 GEN: 2 → 3 nits»), l'avís de portes i,
 * en allargar o escurçar, el preu **total** proposat en un camp editable i el desglossament per nit
 * abans i després (runbook 7, G6: el camp es llegia com a preu per nit). «Confirmar» fa el mateix `PATCH`
 * que el diàleg de la fitxa (`useSaveStay`); si falla (409, 502), toast i tot torna al seu lloc.
 *
 * Preu (`plans/preus-en-crear-reserves.md`, F2): si el preu que es desarà no es pot partir en base i
 * quota al cèntim, surt l'avís amb els dos preus bons més propers (`PriceVatNotice`). No bloqueja.
 */

import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { langToLocale } from '@/lib/dates';
import { useSaveStay, type StayPatch } from '@/hooks/useSaveStay';
import {
  STAY_DATES_NOTICE,
  STAY_NOTICE,
  parsePrice,
  priceInputError,
} from '@/components/reservation-sheet/EditStayDialog';
import { PriceVatNotice } from '@/components/reservations/PriceVatNotice';
import {
  centsToInputValue,
  priceVatNoticeForEdit,
  type PriceVatNotice as PriceVatNoticeData,
} from '@/lib/pricing/price-vat-notice';
import { accommodationVatPercent } from '@/lib/pricing/vat-config';
import { daysBetween, proposePrice, toDay } from './grid/resolve-targets';

// ============================================
// TYPES
// ============================================

interface StayChangeBase {
  reservationId: string;
  beds24Id?: string | null;
  checkIn: string;
  checkOut: string;
}

export interface MoveStayChange extends StayChangeBase {
  kind: 'move';
  fromUnitName: string;
  toUnitId: string;
  toUnitName: string;
}

export interface ResizeStayChange extends StayChangeBase {
  kind: 'resize';
  newCheckOut: string;
  /** Preu total actual (per proposar-ne un de nou). */
  price: number | null;
}

export type StayChange = MoveStayChange | ResizeStayChange;

// ============================================
// HELPERS (purs)
// ============================================

/** `2027-01-16` → `16 GEN`. */
export function formatDayMonth(day: string): string {
  const d = parseISO(toDay(day));
  const month = format(d, 'LLL', { locale: langToLocale('ca') }).replace('.', '').toUpperCase();
  return `${format(d, 'd')} ${month}`;
}

/** `13→15 GEN`, o `30 GEN→2 FEBR` si canvia el mes. */
export function formatStayRange(checkIn: string, checkOut: string): string {
  const a = formatDayMonth(checkIn);
  const b = formatDayMonth(checkOut);
  const [dayA, monthA] = a.split(' ');
  const [, monthB] = b.split(' ');
  return monthA === monthB && toDay(checkIn).slice(0, 4) === toDay(checkOut).slice(0, 4)
    ? `${dayA}→${b}`
    : `${a}→${b}`;
}

function nightsLabel(n: number): string {
  return `${n} ${n === 1 ? 'nit' : 'nits'}`;
}

function reservationLabel(change: StayChange): string {
  return change.beds24Id || 'la reserva';
}

/** La frase de la confirmació. */
export function describeStayChange(change: StayChange): string {
  const nights = daysBetween(change.checkIn, change.checkOut);
  if (change.kind === 'move') {
    return `Moure ${reservationLabel(change)} de ${change.fromUnitName} a ${change.toUnitName} · ${formatStayRange(change.checkIn, change.checkOut)} (${nightsLabel(nights)})`;
  }
  const newNights = daysBetween(change.checkIn, change.newCheckOut);
  const verb = newNights > nights ? 'Allargar' : 'Escurçar';
  return `${verb} ${reservationLabel(change)} fins al ${formatDayMonth(change.newCheckOut)}: ${nights} → ${nightsLabel(newNights)}`;
}

/** Preu proposat per a un canvi de dates (`preu / nits × nits noves`), o `null`. */
export function proposedPriceFor(change: StayChange): number | null {
  if (change.kind !== 'resize') return null;
  return proposePrice(
    change.price,
    daysBetween(change.checkIn, change.checkOut),
    daysBetween(change.checkIn, change.newCheckOut),
  );
}

/** Avís de portes: moure → porta nova; allargar/escurçar → nova sortida, mateix codi. */
export function stayChangeNotice(change: StayChange): string {
  return change.kind === 'move' ? STAY_NOTICE : STAY_DATES_NOTICE;
}

/** `82.5` → `82,50 €`. */
export function formatEuros(value: number): string {
  return `${value.toFixed(2).replace('.', ',')} €`;
}

/** `55 €, 2 nits` → `55,00 € = 27,50 €/nit × 2 nits`, o `null` si no es pot desglossar. */
export function priceBreakdown(total: number | null | undefined, nights: number): string | null {
  if (total == null || !Number.isFinite(total) || total <= 0 || nights < 1) return null;
  const perNight = Math.round((total / nights) * 100) / 100;
  return `${formatEuros(total)} = ${formatEuros(perNight)}/nit × ${nightsLabel(nights)}`;
}

/**
 * «Abans: … · Després: …». El després surt del camp editable: buit = el preu no es toca (el
 * mateix total repartit en les nits noves); no vàlid = sense «després».
 */
export function stayPriceBreakdown(change: ResizeStayChange, priceInput: string): string | null {
  const before = priceBreakdown(change.price, daysBetween(change.checkIn, change.checkOut));
  const typed = parsePrice(priceInput);
  const afterTotal = priceInput.trim() === '' ? change.price : priceInputError(priceInput) === null ? typed : null;
  const after = priceBreakdown(afterTotal, daysBetween(change.checkIn, change.newCheckOut));
  const parts = [before && `Abans: ${before}`, after && `Després: ${after}`].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * El `PATCH`: la unitat en moure; la sortida (i el preu si canvia) en allargar o escurçar. Un preu
 * que no es pot desar (`priceInputError`: no > 0, o més de 2 decimals) no hi va mai, ni arrodonit.
 */
export function buildChangePatch(change: StayChange, priceInput: string): StayPatch {
  if (change.kind === 'move') return { unit_id: change.toUnitId };
  const patch: StayPatch = { data_sortida: toDay(change.newCheckOut) };
  const price = parsePrice(priceInput);
  if (price !== null && priceInputError(priceInput) === null && price !== change.price) patch.preu_total = price;
  return patch;
}

/**
 * L'avís de preu d'un canvi de dates: només si el camp porta un preu que es desarà (vàlid, > 0 i
 * diferent del que la reserva ja té, com a `buildChangePatch`). Moure d'habitació no toca el preu.
 */
export function stayChangeVatNotice(
  change: StayChange,
  priceInput: string,
  vatPercent: number,
): PriceVatNoticeData | null {
  if (change.kind !== 'resize') return null;
  if (priceInputError(priceInput) !== null) return null;
  return priceVatNoticeForEdit(parsePrice(priceInput), change.price, vatPercent);
}

// ============================================
// BODY
// ============================================

export interface StayChangeConfirmBodyProps {
  change: StayChange;
  /** Després de desar o de fallar: el diàleg es tanca i la graella torna a pintar el que hi ha. */
  onDone: () => void;
  onCancel: () => void;
  /**
   * Tipus d'IVA de l'allotjament, en tant per cent, per a l'avís de preu. Per defecte, el general de
   * la configuració (`accommodationVatPercent()`): el gest de la graella no porta la propietat.
   */
  vatPercent?: number;
  /** El text amb què s'obre el camp de preu. Per defecte, el preu proposat. */
  initialPriceInput?: string;
}

export function StayChangeConfirmBody({
  change,
  onDone,
  onCancel,
  vatPercent,
  initialPriceInput,
}: StayChangeConfirmBodyProps) {
  const proposed = proposedPriceFor(change);
  const [priceInput, setPriceInput] = useState(initialPriceInput ?? (proposed != null ? proposed.toFixed(2) : ''));
  const save = useSaveStay({ onSuccess: onDone, onError: onDone });

  const breakdown = change.kind === 'resize' ? stayPriceBreakdown(change, priceInput) : null;
  const vatNotice = stayChangeVatNotice(change, priceInput, vatPercent ?? accommodationVatPercent());
  // «Confirmar» és un botó sense `<form>`: el navegador no valida res, ho fa `priceInputError`.
  const priceError = change.kind === 'resize' ? priceInputError(priceInput) : null;

  const confirm = () => {
    if (priceError) return;
    save.mutate({ reservationId: change.reservationId, patch: buildChangePatch(change, priceInput) });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm font-medium text-gray-900" data-testid="stay-change-summary">
        {describeStayChange(change)}
      </p>

      <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>{stayChangeNotice(change)}</p>
      </div>

      {change.kind === 'resize' && (
        <div className="space-y-1.5">
          <Label htmlFor="stay-change-price">Preu total de l&apos;estada (€)</Label>
          <Input
            id="stay-change-price"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {proposed != null
              ? `Proposat: ${formatEuros(proposed)} per a tota l’estada, al mateix preu per nit. Es pot canviar.`
              : 'La reserva no té preu: si no n’hi poses cap, no es toca.'}
          </p>
          {breakdown && (
            <p className="text-xs text-muted-foreground" data-testid="stay-change-breakdown">
              {breakdown}
            </p>
          )}
          {priceError && <p className="text-xs text-destructive">{priceError}</p>}
          <PriceVatNotice
            notice={vatNotice}
            disabled={save.isPending}
            onPick={(cents) => setPriceInput(centsToInputValue(cents))}
          />
        </div>
      )}

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={save.isPending}>
          Enrere
        </Button>
        <Button type="button" onClick={confirm} disabled={save.isPending || !!priceError}>
          {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Confirmar
        </Button>
      </DialogFooter>
    </div>
  );
}

// ============================================
// DIALOG
// ============================================

export interface StayChangeConfirmDialogProps {
  /** `null` = tancat. */
  change: StayChange | null;
  onClose: () => void;
}

export function StayChangeConfirmDialog({ change, onClose }: StayChangeConfirmDialogProps) {
  return (
    <Dialog open={!!change} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {change?.kind === 'resize' ? 'Canviar la sortida' : "Canviar d'habitació"}
          </DialogTitle>
          <DialogDescription>Res no es desa fins que confirmis.</DialogDescription>
        </DialogHeader>
        {change && (
          <StayChangeConfirmBody
            key={`${change.reservationId}-${change.kind}`}
            change={change}
            onDone={onClose}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default StayChangeConfirmDialog;
